import { supabase } from '../supabaseClient';
import {
  FORFEO_RESERVE_USER_EXPERIENCE_SCHEMA,
  normalizeForfeoReserveUserLocale,
  validateForfeoReserveUserCalendarRequest,
  validateForfeoReserveUserCatalogRequest,
  validateForfeoReserveUserCommand,
  validateForfeoReserveUserResolveRequest,
  validateForfeoReserveUserSnapshotRequest,
  type ForfeoReserveOfficialPassRead,
  type ForfeoReserveUserCalendarRequest,
  type ForfeoReserveUserCatalogItem,
  type ForfeoReserveUserCatalogRequest,
  type ForfeoReserveUserCommand,
  type ForfeoReserveUserLocale,
  type ForfeoReserveUserResolution,
  type ForfeoReserveUserResolveRequest,
  type ForfeoReserveUserSnapshot,
  type ForfeoReserveUserSnapshotRequest,
} from '../../supabase/functions/_shared/forfeo-reserve-user-experience';

export const FORFEO_RESERVE_USER_EXPERIENCE_FEATURE_FLAG =
  'VITE_FORFEO_RESERVE_USER_EXPERIENCE_V1' as const;

export function isForfeoReserveUserExperienceEnabled(): boolean {
  return import.meta.env.VITE_FORFEO_RESERVE_USER_EXPERIENCE_V1 === 'true';
}

export function currentForfeoReserveUserLocale(): ForfeoReserveUserLocale {
  return normalizeForfeoReserveUserLocale(
    document.documentElement.lang || navigator.language,
  );
}

export class ForfeoReserveUserExperienceClientError extends Error {
  readonly code: string;

  constructor(code: string) {
    super(code);
    this.name = 'ForfeoReserveUserExperienceClientError';
    this.code = code;
  }
}

function correlationId(): string {
  return `reserve-user-experience:${crypto.randomUUID()}`;
}

function idempotencyKey(kind: string): string {
  return `reserve:user:${kind}:${crypto.randomUUID()}`;
}

async function invokeUserExperience<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke(
    'reserve-user-experience',
    { body },
  );
  if (error) {
    throw new ForfeoReserveUserExperienceClientError(
      String((data as { error?: string } | null)?.error || 'reserve_user_network_error'),
    );
  }
  if (!data?.ok) {
    throw new ForfeoReserveUserExperienceClientError(
      String(data?.error || 'reserve_user_operation_failed'),
    );
  }
  return data as T;
}

export function createForfeoReserveUserCatalogRequest(
  input: Omit<ForfeoReserveUserCatalogRequest, 'correlationId'>
    & Partial<Pick<ForfeoReserveUserCatalogRequest, 'correlationId'>>,
): ForfeoReserveUserCatalogRequest {
  const request: ForfeoReserveUserCatalogRequest = {
    ...input,
    correlationId: input.correlationId || correlationId(),
  };
  const errors = validateForfeoReserveUserCatalogRequest(request);
  if (errors.length > 0) throw new ForfeoReserveUserExperienceClientError(errors.join(','));
  return request;
}

export function createForfeoReserveUserResolveRequest(
  input: Omit<ForfeoReserveUserResolveRequest, 'correlationId'>
    & Partial<Pick<ForfeoReserveUserResolveRequest, 'correlationId'>>,
): ForfeoReserveUserResolveRequest {
  const request: ForfeoReserveUserResolveRequest = {
    ...input,
    correlationId: input.correlationId || correlationId(),
  };
  const errors = validateForfeoReserveUserResolveRequest(request);
  if (errors.length > 0) throw new ForfeoReserveUserExperienceClientError(errors.join(','));
  return request;
}

export function createForfeoReserveUserSnapshotRequest(
  input: Omit<ForfeoReserveUserSnapshotRequest, 'correlationId'>
    & Partial<Pick<ForfeoReserveUserSnapshotRequest, 'correlationId'>>,
): ForfeoReserveUserSnapshotRequest {
  const request: ForfeoReserveUserSnapshotRequest = {
    ...input,
    correlationId: input.correlationId || correlationId(),
  };
  const errors = validateForfeoReserveUserSnapshotRequest(request);
  if (errors.length > 0) throw new ForfeoReserveUserExperienceClientError(errors.join(','));
  return request;
}

export function createForfeoReserveUserCommand(
  input: Omit<ForfeoReserveUserCommand, 'correlationId' | 'idempotencyKey'>
    & Partial<Pick<ForfeoReserveUserCommand, 'correlationId' | 'idempotencyKey'>>,
): ForfeoReserveUserCommand {
  const command: ForfeoReserveUserCommand = {
    ...input,
    correlationId: input.correlationId || correlationId(),
    idempotencyKey: input.idempotencyKey || idempotencyKey(input.kind),
  };
  const errors = validateForfeoReserveUserCommand(command);
  if (errors.length > 0) throw new ForfeoReserveUserExperienceClientError(errors.join(','));
  return command;
}

export async function fetchForfeoReserveUserCatalog(
  request: ForfeoReserveUserCatalogRequest,
): Promise<ForfeoReserveUserCatalogItem[]> {
  const errors = validateForfeoReserveUserCatalogRequest(request);
  if (errors.length > 0) throw new ForfeoReserveUserExperienceClientError(errors.join(','));
  const data = await invokeUserExperience<{
    ok: true;
    schema: typeof FORFEO_RESERVE_USER_EXPERIENCE_SCHEMA;
    catalog: ForfeoReserveUserCatalogItem[];
  }>({ action: 'catalog', request });
  return data.catalog;
}

export async function resolveForfeoReserveUserSelection(
  request: ForfeoReserveUserResolveRequest,
): Promise<ForfeoReserveUserResolution> {
  const errors = validateForfeoReserveUserResolveRequest(request);
  if (errors.length > 0) throw new ForfeoReserveUserExperienceClientError(errors.join(','));
  const data = await invokeUserExperience<{ ok: true; resolution: ForfeoReserveUserResolution }>({
    action: 'resolve',
    request,
  });
  return data.resolution;
}

export async function fetchForfeoReserveUserSnapshot(
  request: ForfeoReserveUserSnapshotRequest,
): Promise<ForfeoReserveUserSnapshot> {
  const errors = validateForfeoReserveUserSnapshotRequest(request);
  if (errors.length > 0) throw new ForfeoReserveUserExperienceClientError(errors.join(','));
  const data = await invokeUserExperience<{ ok: true; snapshot: ForfeoReserveUserSnapshot }>({
    action: 'snapshot',
    request,
  });
  return data.snapshot;
}

export async function applyForfeoReserveUserCommand(
  command: ForfeoReserveUserCommand,
): Promise<Record<string, unknown>> {
  const errors = validateForfeoReserveUserCommand(command);
  if (errors.length > 0) throw new ForfeoReserveUserExperienceClientError(errors.join(','));
  const data = await invokeUserExperience<{ ok: true; result: Record<string, unknown> }>({
    action: 'command',
    command,
  });
  return data.result;
}

export async function createForfeoReserveFinancialCheckout(input: {
  holdId: string;
  expectedRevision: number;
  successUrl: string;
  cancelUrl: string;
}): Promise<{ checkoutUrl: string; intentId: string; bindingId: string }> {
  const { data, error } = await supabase.functions.invoke(
    'issue-reserve-giftshop-intent',
    {
      body: {
        ...input,
        idempotencyKey: idempotencyKey('financial_checkout'),
        correlationId: correlationId(),
      },
    },
  );
  if (error || !data?.ok || typeof data.checkoutUrl !== 'string') {
    throw new ForfeoReserveUserExperienceClientError(
      String(data?.error || 'giftshop_checkout_unavailable'),
    );
  }
  return {
    checkoutUrl: data.checkoutUrl,
    intentId: String(data.intentId || ''),
    bindingId: String(data.bindingId || ''),
  };
}

export async function downloadForfeoReserveCalendar(
  request: ForfeoReserveUserCalendarRequest,
): Promise<{ filename: string; content: string; mimeType: string }> {
  const complete: ForfeoReserveUserCalendarRequest = {
    ...request,
    correlationId: request.correlationId || correlationId(),
  };
  const errors = validateForfeoReserveUserCalendarRequest(complete);
  if (errors.length > 0) throw new ForfeoReserveUserExperienceClientError(errors.join(','));
  const data = await invokeUserExperience<{
    ok: true;
    calendar: { filename: string; content: string; mimeType: string };
  }>({ action: 'calendar', request: complete });
  return data.calendar;
}

export async function fetchForfeoReserveOfficialPass(
  request: ForfeoReserveUserCalendarRequest,
): Promise<ForfeoReserveOfficialPassRead> {
  const complete: ForfeoReserveUserCalendarRequest = {
    ...request,
    correlationId: request.correlationId || correlationId(),
  };
  const errors = validateForfeoReserveUserCalendarRequest(complete);
  if (errors.length > 0) throw new ForfeoReserveUserExperienceClientError(errors.join(','));
  const data = await invokeUserExperience<{ ok: true; pass: ForfeoReserveOfficialPassRead }>({
    action: 'pass',
    request: complete,
  });
  return data.pass;
}

export function saveForfeoReserveCalendarFile(file: {
  filename: string;
  content: string;
  mimeType: string;
}): void {
  const blob = new Blob([file.content], { type: file.mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = file.filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
